/**
 * Routing helper for solo vs. household-shared data paths.
 *
 * When a user is paired in a household, shared surfaces (meal plan, grocery
 * list, inventory, saved recipes, ratings, preferences) read/write under
 * `households/{householdId}/...` instead of `users/{uid}/...`. This module is
 * the single source of truth for that decision.
 */

export type DataScope =
  | { kind: 'user'; uid: string }
  | { kind: 'household'; householdId: string };

/**
 * Returns the active data scope for sync read/writes, or null if neither a
 * uid nor householdId is available (e.g. guest mode pre-auth).
 *
 * If `householdId` is set, household scope wins. Otherwise falls back to user
 * scope using the uid.
 */
export const resolveDataScope = (
  uid: string | null | undefined,
  householdId: string | null | undefined
): DataScope | null => {
  if (householdId) {
    return { kind: 'household', householdId };
  }
  if (uid) {
    return { kind: 'user', uid };
  }
  return null;
};

/**
 * Builds the Firestore path segments for a given scope and sub-path.
 *
 * Example:
 *   buildScopedPath({kind:'user', uid:'u1'}, 'mealPlans', 'current')
 *     => ['users', 'u1', 'mealPlans', 'current']
 *   buildScopedPath({kind:'household', householdId:'h1'}, 'mealPlans', 'current')
 *     => ['households', 'h1', 'mealPlans', 'current']
 */
export const buildScopedPath = (
  scope: DataScope,
  ...segments: string[]
): string[] => {
  if (scope.kind === 'household') {
    return ['households', scope.householdId, ...segments];
  }
  return ['users', scope.uid, ...segments];
};

/**
 * Convenience: resolve scope and build a path in one call. Returns null if
 * scope can't be resolved.
 */
export const resolveScopedPath = (
  uid: string | null | undefined,
  householdId: string | null | undefined,
  ...segments: string[]
): string[] | null => {
  const scope = resolveDataScope(uid, householdId);
  if (!scope) return null;
  return buildScopedPath(scope, ...segments);
};
